import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  Text,
  View,
  ScrollView,
  StyleSheet,
  AppState,
  Linking,
  TouchableOpacity,
} from 'react-native';
import {collectUserBehavior} from '../API/newHome';

interface PageDuration {
  pageName: string;
  startTime: number;
  duration: number;
  endTime: number;
  screenOffDuration?: number;
  screenOffTime?: number;
  screenOnTime?: number;
  jumpToApp?: {
    // 新增：跳转到其他APP的信息
    url: string; // 跳转的URL
    startTime: number; // 跳转开始时间
    endTime?: number; // 返回时间
    duration?: number; // 跳转持续时间
  };
}

interface PageTrackContextType {
  theme: string;
  setTheme: (theme: string) => void;
  currentPage: string | undefined;
  setCurrentPage: (pageName: string | undefined) => void;
  pageDurations: PageDuration[];
  getCurrentPageDuration: () => number;
}

const PageTrackContext = createContext<PageTrackContextType | undefined>(
  undefined,
);

export function PageTrackProvider({children}: {children: ReactNode}) {
  const [theme, setTheme] = useState('light');
  const [currentPage, setCurrentPage] = useState<string | undefined>();
  const [pageDurations, setPageDurations] = useState<PageDuration[]>([]);
  const pageStartTimeRef = useRef<number>(0);
  const lastPageRef = useRef<string | undefined>();
  const screenOffTimeRef = useRef<number>(0);
  const jumpStartTimeRef = useRef<number>(0);
  const jumpUrlRef = useRef<string>('');
  const [expandedItems, setExpandedItems] = useState<{[key: number]: boolean}>(
    {},
  );

  // 使用 useMemo 缓存 toolMaps
  const toolMaps = useMemo(
    () => ({
      ChatGPTTeacher: [
        'ChatGPTTeacher',
        'TakePhotoPage',
        'OCRSuccess',
        'OCREdit',
      ],
      CmsWebView: ['CmsWebView'],
      MyLearnBeikao: [
        'MyLearnBeikao',
        'MyLearnBeikaoMingxiao',
        'MyLearnBeikaoJieduan',
        'MyLearnBeikaoZhuanti',
      ],
      MyLearn: [
        'MyLearn',
        'SynchroWorkbook',
        'WorkbookDetails',
        'InitialSubjectTest',
        'TestDetails',
      ],
      MyLearnCenter: [
        'MyLearnCenter',
        'PaperDetails',
        'SubmitAnswer',
        'AnswerReport',
        'WorkBookParse',
      ],
      WrongQuestion: [
        'WrongQuestion',
        'TagManger',
        'WrongQuestionList',
        'Arrangement',
        'WrongDownload',
        'WrongStatistics',
      ],
      AIExplore: ['AIExplore'],
      UserCenter: [
        'UserCenter',
        'RecordIndex',
        'ChatRecordInfo',
        'ChangeUser',
        'SettingList',
        'AboutOnePad',
        'YinSiDocument',
        'FuWuDocument',
        'Register',
        'ForgetPassword',
        'ParentBinding',
      ],
      Home: ['ChatGPTAssist', 'MiddleHomePage'],
    }),
    [],
  ); // 空依赖数组，因为 toolMaps 是静态的

  // 获取工具代码的函数
  const getToolCode = useCallback(
    (pageName: string) => {
      for (const [code, pages] of Object.entries(toolMaps)) {
        if (pages.includes(pageName)) {
          return code;
        }
      }
      return null;
    },
    [toolMaps],
  );

  // 移除 console.log
  // 优化 handleScreenStateChange 的依赖
  const handleScreenStateChange = useCallback(
    (isScreenOn: boolean, timestamp: number) => {
      if (!isScreenOn) {
        screenOffTimeRef.current = timestamp;
        if (currentPage && pageStartTimeRef.current > 0) {
          const duration = timestamp - pageStartTimeRef.current;
          const durationInSeconds = Math.floor(duration / 1000);

          // 在息屏前记录并上报数据
          if (durationInSeconds > 0) {
            setPageDurations(prev => [
              ...prev,
              {
                pageName: currentPage,
                startTime: pageStartTimeRef.current,
                endTime: timestamp,
                duration,
                screenOffTime: timestamp,
              },
            ]);

            // 调用接口记录息屏前的使用时长
            const toolCode = getToolCode(currentPage);
            if (toolCode && currentPage !== 'Login') {
              collectUserBehavior({
                code: toolCode,
                useDuration: durationInSeconds,
              });
            }
          }
        }
        pageStartTimeRef.current = 0;
      } else {
        const screenOffDuration = timestamp - screenOffTimeRef.current;
        setPageDurations(prev => {
          const lastRecord = prev[prev.length - 1];
          if (lastRecord?.screenOffTime) {
            return [
              ...prev.slice(0, -1),
              {
                ...lastRecord,
                screenOffDuration,
                screenOnTime: timestamp,
              },
            ];
          }
          return prev;
        });

        if (currentPage) {
          pageStartTimeRef.current = timestamp;
        }
        screenOffTimeRef.current = 0;
      }
    },
    [currentPage, getToolCode],
  );

  useEffect(() => {
    let lastAppState = AppState.currentState;

    const subscription = AppState.addEventListener('change', nextAppState => {
      const now = Date.now();

      if (
        lastAppState === 'active' &&
        (nextAppState === 'background' || nextAppState === 'inactive')
      ) {
        handleScreenStateChange(false, now);
      } else if (
        (lastAppState === 'background' || lastAppState === 'inactive') &&
        nextAppState === 'active'
      ) {
        handleScreenStateChange(true, now);
      }

      lastAppState = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [handleScreenStateChange]);

  // URL 监听逻辑
  useEffect(() => {
    Linking.getInitialURL().then(url => {
      console.log('wo shi url', url);
      if (url) {
        jumpUrlRef.current = url;
        jumpStartTimeRef.current = Date.now();
      }
    });

    const subscription = Linking.addEventListener('url', ({url}) => {
      const now = Date.now();

      if (!jumpStartTimeRef.current) {
        jumpUrlRef.current = url;
        jumpStartTimeRef.current = now;

        if (currentPage && pageStartTimeRef.current > 0) {
          const duration = now - pageStartTimeRef.current;
          setPageDurations(prev => [
            ...prev,
            {
              pageName: currentPage,
              startTime: pageStartTimeRef.current,
              endTime: now,
              duration,
              jumpToApp: {
                url,
                startTime: now,
              },
            },
          ]);
        }
        pageStartTimeRef.current = 0;
      } else {
        const jumpDuration = now - jumpStartTimeRef.current;
        setPageDurations(prev => {
          const lastRecord = prev[prev.length - 1];
          if (lastRecord && lastRecord.jumpToApp) {
            const updatedRecord = {
              ...lastRecord,
              jumpToApp: {
                ...lastRecord.jumpToApp,
                endTime: now,
                duration: jumpDuration,
              },
            };
            return [...prev.slice(0, -1), updatedRecord];
          }
          return prev;
        });

        jumpStartTimeRef.current = 0;
        jumpUrlRef.current = '';

        if (currentPage) {
          pageStartTimeRef.current = now;
        }
      }
    });

    return () => {
      subscription.remove();
    };
  }, [currentPage]);

  // 添加页面切换的效果
  useEffect(() => {
    const now = Date.now();

    if (lastPageRef.current && pageStartTimeRef.current > 0) {
      const duration = now - pageStartTimeRef.current;
      const durationInSeconds = Math.floor(duration / 1000);

      // 只有当转换为秒后大于 0 时才记录和收集数据
      if (durationInSeconds > 0) {
        const newDuration: PageDuration = {
          pageName: lastPageRef.current,
          startTime: pageStartTimeRef.current,
          endTime: now,
          duration,
        };

        setPageDurations(prev => [...prev, newDuration]);

        // 在页面切换时收集行为数据
        const toolCode = getToolCode(lastPageRef.current);
        if (toolCode && lastPageRef.current !== 'Login') {
          collectUserBehavior({
            code: toolCode,
            useDuration: durationInSeconds,
          });
        }
      }
    }

    if (currentPage) {
      pageStartTimeRef.current = now;
      lastPageRef.current = currentPage;
    }
  }, [currentPage, getToolCode]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getCurrentPageDuration = () => {
    if (!currentPage || !pageStartTimeRef.current) {
      return 0;
    }
    return Date.now() - pageStartTimeRef.current;
  };

  // 切换折叠状态
  const toggleExpand = (index: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // 优化 value 对象的创建
  const value = useMemo(
    () => ({
      theme,
      setTheme,
      currentPage,
      setCurrentPage,
      pageDurations,
      getCurrentPageDuration,
    }),
    [theme, currentPage, pageDurations, getCurrentPageDuration],
  );

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
  };

  const formatDuration = (duration: number) => {
    if (duration < 1000) {
      return `${duration}ms`;
    }
    return `${(duration / 1000).toFixed(1)}s`;
  };

  return (
    <PageTrackContext.Provider value={value}>
      {pageDurations.length > 0 && false && (
        <ScrollView style={styles.container}>
          {pageDurations.map((record, index) => (
            <View key={index} style={styles.recordItem}>
              <TouchableOpacity
                onPress={() => toggleExpand(index)}
                style={styles.header}>
                <Text style={styles.pageName}>页面：{record.pageName}</Text>
                <Text style={styles.duration}>
                  停留：{formatDuration(record.duration)}
                </Text>
              </TouchableOpacity>

              {expandedItems[index] && (
                <View style={styles.details}>
                  <Text style={styles.timeInfo}>
                    开始：{formatTime(record.startTime)}
                  </Text>
                  <Text style={styles.timeInfo}>
                    结束：{formatTime(record.endTime)}
                  </Text>

                  {record.screenOffTime && (
                    <View style={styles.screenOffContainer}>
                      <Text style={styles.screenOffInfo}>
                        息屏时间：{formatTime(record.screenOffTime)}
                      </Text>
                      {record.screenOnTime && (
                        <Text style={styles.screenOffInfo}>
                          亮屏时间：{formatTime(record.screenOnTime)}
                        </Text>
                      )}
                      {record.screenOffDuration && (
                        <Text
                          style={[styles.duration, styles.screenOffDuration]}>
                          息屏时长：{formatDuration(record.screenOffDuration)}
                        </Text>
                      )}
                    </View>
                  )}

                  {record.jumpToApp && (
                    <View style={styles.jumpContainer}>
                      <Text style={styles.jumpInfo}>
                        跳转到：{record.jumpToApp.url}
                      </Text>
                      <Text style={styles.jumpInfo}>
                        跳转时间：{formatTime(record.jumpToApp.startTime)}
                      </Text>
                      {record.jumpToApp.endTime && (
                        <>
                          <Text style={styles.jumpInfo}>
                            返回时间：{formatTime(record.jumpToApp.endTime)}
                          </Text>
                          <Text style={[styles.duration, styles.jumpDuration]}>
                            跳转时长：
                            {formatDuration(record.jumpToApp.duration!)}
                          </Text>
                        </>
                      )}
                    </View>
                  )}
                </View>
              )}
            </View>
          ))}
        </ScrollView>
      )}
      {children}
    </PageTrackContext.Provider>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 100,
    right: 10,
    maxHeight: 400,
    width: 300,
    backgroundColor: 'rgba(0,0,0,0.8)',
    zIndex: 9999,
    borderRadius: 8,
    padding: 8,
  },
  recordItem: {
    marginBottom: 8,
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  pageName: {
    color: '#fff',
    fontWeight: 'bold',
    marginBottom: 4,
    flex: 1,
    marginRight: 8,
  },
  timeInfo: {
    color: '#ccc',
    fontSize: 12,
    flexShrink: 1,
  },
  duration: {
    color: '#4CAF50',
    marginTop: 4,
    flexShrink: 0,
  },
  screenOffInfo: {
    color: '#ff9800',
    fontSize: 12,
    marginTop: 4,
    flexShrink: 1,
  },
  screenOffDuration: {
    color: '#ff5722',
  },
  jumpInfo: {
    color: '#2196F3',
    fontSize: 12,
    marginTop: 4,
    flexShrink: 1,
  },
  jumpDuration: {
    color: '#1976D2',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    flexWrap: 'wrap',
  },
  details: {
    marginTop: 4,
    paddingLeft: 8,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255,255,255,0.1)',
  },
  screenOffContainer: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.05)',
  },
  jumpContainer: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.05)',
  },
});

export function usePageTrack() {
  const context = useContext(PageTrackContext);
  if (context === undefined) {
    throw new Error('usePageTrack must be used within a PageTrackProvider');
  }
  return context;
}
